import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, setUnauthorisedHandler, storeToken, storedToken } from '../api/client'
import type { CurrentUser, LoginResponse } from '../api/types'
import { clearApiCache } from '../hooks/useApi'

interface AuthState {
  user: CurrentUser | null
  ready: boolean
  isOwner: boolean
  signIn: (username: string, pin: string) => Promise<void>
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [ready, setReady] = useState(false)

  /*
   * Whoever is next at this phone starts from nothing.
   *
   * The remembered lists carry customer names and what each of them owes, so
   * they have to go the moment the owner hands the phone back - and the same
   * on a token the server has stopped accepting, which is the other way
   * somebody stops being signed in.
   */
  const signOut = useCallback(() => {
    clearApiCache()
    storeToken(null)
    setUser(null)
  }, [])

  useEffect(() => {
    setUnauthorisedHandler(() => {
      clearApiCache()
      setUser(null)
    })
  }, [])

  // A stored token is a claim, not a fact. Ask the server who it belongs to
  // before showing anybody a book.
  useEffect(() => {
    if (!storedToken()) {
      setReady(true)
      return
    }
    api
      .get<CurrentUser>('/auth/me')
      .then((response) => setUser(response.data))
      .catch(() => storeToken(null))
      .finally(() => setReady(true))
  }, [])

  const signIn = useCallback(async (username: string, pin: string) => {
    clearApiCache()
    const response = await api.post<LoginResponse>('/auth/login', { username, pin })
    storeToken(response.data.token)
    setUser(response.data.user)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, ready, isOwner: user?.role === 'OWNER', signIn, signOut }),
    [user, ready, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
